import { PUBLIC_COMPS_APPROACH } from 'common/constants/calibration/calibration';
import { FinancialStatementsBasic } from 'common/types/financials';
import { Calibration, CustomCalibrationPerformanceModel, ValuationsApproach } from 'common/types/valuation';
import { PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import { PerformanceMetricsColumn } from 'pages/ValuationsAllocation/approaches/TransactionComps/PerformanceMetrics/types';
import { transposeFinancials } from 'pages/ValuationsAllocation/util';
import { getDefaultCompsApproachForDropdown } from 'pages/ValuationsAllocation/util/getDefaultCompsApproachForDropdown';
import { FinancialsTransposed } from 'pages/ValuationsAllocation/util/transposeFinancials/types';
import { getNumberValue, getObjectValue } from 'utilities';
import { CalibrationComparison } from './performance_metrics/config/rowConfig/types';

export const createNewPerformance = (idCalibration: number): CustomCalibrationPerformanceModel => ({
  name: '_',
  enterprise_value: '',
  ltm_revenue: '',
  ltm_revenue_multiple: '',
  ltm_revenue_growth: '',
  ntm_revenue: '',
  ntm_revenue_multiple: '',
  ntm_revenue_growth: '',
  ltm_ebitda: '',
  ltm_ebitda_multiple: '',
  ntm_ebitda: '',
  ntm_ebitda_multiple: '',
  gross_margin: '',
  ebitda_margin: '',
  tangible_book_value: '',
  net_income_to_common_excl_extra_items: '',
  cap_iq_id: '',
  calibration: idCalibration,
});

export const decimalResults = (valueString: string, decimalIndex: number, decimalPlaces: number): number => {
  if (decimalIndex !== -1) {
    const first10Decimals = valueString.substring(0, decimalIndex + decimalPlaces);
    return parseFloat(first10Decimals);
  }
  return parseFloat(valueString) ?? 0;
};

const DEFAULT_DECIMAL_PLACES = 11;
const SPECIFIC_FIELDS_DECIMAL_PLACES = 3;
const SPECIFIC_FIELDS = ['ltm_revenue', 'ltm_ebitda', 'ntm_revenue', 'ntm_ebitda'];

export interface PerformanceCalculatorArgs {
  numCols: number;
  numRows: number;
  performanceMetricsKeys: PerformanceMetricsColumn[];
  performanceKeys: string[];
  idCalibration: number;
  calibration: Calibration;
}

export const PerformanceCalculator = ({
  numCols,
  numRows,
  performanceMetricsKeys,
  performanceKeys,
  idCalibration,
  calibration,
}: PerformanceCalculatorArgs): CustomCalibrationPerformanceModel[] => {
  for (let colIndex = 0; colIndex < numCols; colIndex++) {
    const newPerformance = createNewPerformance(idCalibration);

    for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
      const key = Object.keys(performanceMetricsKeys[rowIndex])[colIndex];
      const decimalValue = performanceMetricsKeys[rowIndex][key].value;
      const field = performanceKeys[rowIndex] as keyof CustomCalibrationPerformanceModel;

      const decimalPlaces = SPECIFIC_FIELDS.includes(field) ? SPECIFIC_FIELDS_DECIMAL_PLACES : DEFAULT_DECIMAL_PLACES;

      if (!isNaN(Number(decimalValue))) {
        const valueString = decimalValue?.toString() ?? '';
        const decimalIndex = valueString.indexOf('.');

        (newPerformance as any)[field] = decimalResults(valueString, decimalIndex, decimalPlaces);
      } else {
        newPerformance.name = decimalValue as string;
      }
    }
    const { name } = newPerformance;
    if ((typeof name === 'number' && name !== 0) || typeof name === 'string') {
      calibration.performances?.push(newPerformance);
    }
  }
  return calibration.performances ?? [];
};

// Types for compsWithMetrics
export interface CompWithMetric {
  cap_iq_id: string | number;
  custom_date_performance_metrics: Array<{ id: string | number }>;
  [key: string]: any;
}

export const updateCompsWithMetrics = (
  compsWithMetrics: CompWithMetric[],
  correspondingCompsWithMetrics: CompWithMetric[]
): CompWithMetric[] => {
  let updatedCompsWithMetrics = [...compsWithMetrics];

  updatedCompsWithMetrics = updatedCompsWithMetrics.map(compWithMetric => {
    const correspondingComp = correspondingCompsWithMetrics.find(comp => comp.cap_iq_id === compWithMetric.cap_iq_id);

    if (correspondingComp && correspondingComp?.custom_date_performance_metrics?.length > 0) {
      const correspondingMetric = correspondingComp.custom_date_performance_metrics[0];

      if (compWithMetric.custom_date_performance_metrics.length > 0) {
        // eslint-disable-next-line no-param-reassign
        compWithMetric.custom_date_performance_metrics[0].id = correspondingMetric.id;
      }
    }

    return compWithMetric;
  });

  return updatedCompsWithMetrics;
};

type DetermineValueProps = {
  current: CalibrationComparison;
  property: string;
  calculateValue?: (current: CalibrationComparison) => number | string;
  auxProperty?: string;
};

export const determineValue = ({ current, property, calculateValue, auxProperty }: DetermineValueProps): any => {
  if (calculateValue) {
    return calculateValue(current);
  }

  const primaryValue = current[property as keyof CalibrationComparison];
  const auxiliaryValue = auxProperty ? current[auxProperty as keyof CalibrationComparison] : undefined;

  if (typeof primaryValue === 'number' || typeof auxiliaryValue === 'number') {
    return getNumberValue(primaryValue ?? auxiliaryValue);
  }

  return primaryValue ?? auxiliaryValue;
};

export const getFinancials = (
  financials_version: number | string,
  financialStatementsList: Array<{ id: number; [key: string]: any }>
): FinancialsTransposed => {
  const financial = financials_version;
  const selectedFinancialVersion = financialStatementsList.filter(tab => tab.id === Number(financial));
  const financialStatement = getObjectValue(selectedFinancialVersion[0]) as FinancialStatementsBasic;
  return transposeFinancials(financialStatement);
};

export const getFinalValueByAlias = (
  value: any,
  gridType: string,
  calibrationData: Record<string, any>,
  alias: string
): any => {
  let result = value;
  if (alias === PUBLIC_COMPS_APPROACH && value === '') {
    return calibrationData.temp_public_comps_reference || calibrationData[alias];
  }
  if (value === '' || value === 0) {
    if (gridType === 'number') {
      result = calibrationData[alias] ?? 0;
    } else {
      result = calibrationData[alias] ?? '';
    }
  }

  return result;
};

export interface PlusDebtAndLessCashInputsTableResult {
  gridType: string;
  readOnly: boolean;
  value: any;
}

export const getPlusDebtAndLessCashInputsTable = (
  isDisabled: boolean,
  isLastColumn: boolean,
  gridType: string,
  calibrationData: Record<string, any>,
  readOnly: boolean,
  value: any,
  valueType: boolean
): PlusDebtAndLessCashInputsTableResult => {
  let newGridType = gridType;
  let newReadOnly = readOnly;
  let newValue = value;

  if (isLastColumn) {
    newGridType = 'number';
  }
  if (Number(calibrationData.financials_version) === 0) {
    if (isDisabled === false) {
      newReadOnly = false;
    }

    newValue = valueType ? calibrationData.less_debt_inputs : calibrationData.plus_cash_inputs;
  }

  return {
    gridType: newGridType,
    readOnly: newReadOnly,
    value: newValue,
  };
};

export interface Approach {
  id?: number;
  approach_type?: string;
  name?: string;
  panelId?: string;
  [key: string]: any;
}

/**
 * Reads persisted choice for comps source or returns a default one based on what's available
 * @param public_comps_approach_calibration
 * @param approach
 * @param approaches
 * @returns
 */
export const findCompsSourceApproach = (
  public_comps_approach_calibration: number | null,
  approach: Approach,
  approaches?: ValuationsApproach[]
): Approach => {
  let newApproach = approach;
  if (public_comps_approach_calibration) {
    const selectedApproach = (approaches ?? []).find(
      approach => approach.id === Number(public_comps_approach_calibration)
    );
    if (selectedApproach !== undefined) {
      newApproach = selectedApproach;
    }
  } else {
    const defaultApproach = getDefaultCompsApproachForDropdown(approaches, [PUBLIC_COMPANIES]);
    newApproach = {
      ...defaultApproach,
    };
  }

  return newApproach;
};

export interface CalibrationData {
  financials_version?: number | string;
  ltm_revenue_inputs?: number;
  ntm_revenue_inputs?: number;
  ltm_ebitda_inputs?: number;
  ntm_ebitda_inputs?: number;
  [key: string]: any;
}

export const findSelectedCalibrationFinancial = (
  CalibrationData: CalibrationData,
  financialStatementsList: Array<{ id: number; name?: string; [key: string]: any }>,
  financials: FinancialsTransposed
): { financials: FinancialsTransposed; version: number | string | undefined } => {
  const version = CalibrationData.financials_version;
  let newFinancials = financials;
  const selectedFinancial = financialStatementsList?.find(x => x.id === Number(version) && version !== undefined) || {
    approach_type: '',
    name: '',
  };

  if (selectedFinancial.name !== '') {
    const financialStatement = getObjectValue(selectedFinancial) as FinancialStatementsBasic;
    newFinancials = transposeFinancials(financialStatement);
  } else {
    newFinancials.ltm_revenue = CalibrationData.ltm_revenue_inputs ?? 0;
    newFinancials.ntm_revenue = CalibrationData.ntm_revenue_inputs ?? 0;
    newFinancials.ltm_ebitda = CalibrationData.ltm_ebitda_inputs ?? 0;
    newFinancials.ntm_ebitda = CalibrationData.ntm_ebitda_inputs ?? 0;
  }

  return {
    financials: newFinancials,
    version,
  };
};

export const getPerformanceMetricsKeys = (
  columnsPerformance: Array<Record<string, { enabled: boolean }>>,
  prefix: string
): string[] =>
  columnsPerformance.flatMap(dict =>
    Object.keys(dict).filter(key => key.startsWith(prefix) && !key.includes('current_company') && dict[key].enabled)
  );

export const getCalibratedKeys = (keys: string[], panelIdNew: string, suffix: string): string[] =>
  keys.filter(key => key.includes(suffix)).map(key => `calibration_performance_${panelIdNew}.${key}`);

export const generateOrderMapForComps = (performances: { cap_iq_id?: string | null }[]) => {
  const orderMap = new Map<string, number>();
  performances.forEach((performance, index) => {
    if (performance.cap_iq_id) {
      orderMap.set(performance.cap_iq_id.toString(), index);
    }
  });
  return orderMap;
};

export const readDisabledPerformances = ({ calibration }: { calibration: Calibration }) => {
  const disabledPerformances = calibration?.performances?.filter(performance => !performance.enabled);
  const disabledNames = disabledPerformances?.map(performance => performance.name);
  const disabledIdentifiers = disabledPerformances?.map(performance => performance.cap_iq_id).filter(Boolean);

  return { disabledNames, disabledIdentifiers };
};
